// kfe-audio-player.js — iOS-canon audio control bar (Brief 138).
//
// PORT TARGET: KFEAudioPlayerCombineControlView (iOS) — slider + previous /
// play / next buttons + volume — augmented with the playcount badge and the
// ±15-second skip buttons from Chris's 2026-06-09 annotated player screenshot
// (the canonical source for this brief; Rule #9).
//
// This is a PRESENTATIONAL widget: it owns its own <audio> element and the
// transport chrome, but it does NOT know about lessons, gating, or routing.
// The host screen (kfe-audio-exercise) resolves the playcount + prev/next-item
// context and feeds it in via props, and reacts to the events emitted here:
//   • `ended`     — the track finished (one play). Host persists completion.
//   • `prev-item` — learner tapped the « button (only when prevEnabled).
//   • `next-item` — learner tapped the » button (only when nextEnabled).
//
// Layout (matching the screenshot, within web-platform constraints):
//   ┌─────────────────────────────────────────────── badge: completed/required
//   │  ───────────────●───────────────   (scrubber)
//   │  0:42                        3:18   (time / duration)
//   │     «   −15   ▶   +15   »           (prev-item, skip, play, skip, next-item)
//
// DOM is built ONCE and updated imperatively — re-rendering via the base
// `replaceChildren` would destroy the live <audio> element and interrupt
// playback (cf. kfe-music-track's same constraint). `render()` therefore
// returns undefined (imperative-render path in KFEComponent) and every prop
// change / timeupdate funnels through `_sync()`.

import { KFEComponent } from "../core/component.js";
import { t } from "../core/i18n.js";

/** Seconds skipped by the ±15 buttons (iOS canon). */
const SKIP_SECONDS = 15;

/**
 * iOS-canon skip icons: a ~3/4 arc with an arrowhead at one end, the "15" text
 * centered inside. Back has the arrowhead at the top-LEFT pointing left;
 * forward has the arrowhead at the top-RIGHT pointing right (mirror). Both use
 * `currentColor` so they inherit the button's text colour for hover/focus/
 * disabled states. ViewBox is 40×40; CSS scales them to ~78% of the button.
 */
// Arc geometry note: start (12,9) → end (28,9), radius 13. SVG picks the
// center based on (large-arc, sweep). For an arc that DIPS DOWN through the
// bottom of the viewBox (the U-shape opening at top), the center sits BELOW
// the chord at (20, ~19.25), and the traversal direction is CCW for back
// (left-to-right via bottom) / CW for forward (right-to-left via bottom).
// First pass used the wrong sweep, which selected the OTHER center sitting
// above the viewBox at (20, -1.25); the arc then bulged UP and out of view,
// leaving only the two endpoint stubs visible at the top of each button —
// exactly the symptom Chris reported on the 2026-06-09 smoke (tiny "v" ticks
// with no visible arc around the 15). Fix: flip both sweep flags.
// Arrowhead is a FILLED triangle aligned along the arc's RADIAL direction
// at the endpoint — so the triangle's spine points outward from the arc's
// center (which sits BELOW the chord at (20, 19.25)), and its base is
// perpendicular to that radial, passing through the arc's start point. This
// makes the arrowhead read as a continuous sweep of the arc rather than
// a perpendicular tab sticking off the side (Chris 2026-06-09 screenshot:
// "the arrowhead needs to align with the line").
//
// Geometry (skip-back; forward is a mirror about x=20):
//   • Arc start at (12, 9), center at (20, 19.25), radius 13.
//   • Radial from center to (12,9) → direction (-0.615, -0.788), ~52° above
//     horizontal-left.
//   • Tip placed ~5.5 units outward along that radial: (8, 4).
//   • Base endpoints ~4 units perpendicular to spine through (12,9):
//     (10, 11) on the down-left side, (14, 7) on the up-right side. The arc
//     start (12,9) sits on the line between them, so arc + arrowhead share
//     a point and read as one shape.
const SKIP_BACK_SVG = `<svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">
  <polygon points="8 4, 10 11, 14 7" fill="currentColor"/>
  <path d="M 12 9 A 13 13 0 1 0 28 9" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round"/>
  <text x="20" y="26" text-anchor="middle" font-size="12" font-weight="700" fill="currentColor" font-family="inherit">15</text>
</svg>`;
const SKIP_FORWARD_SVG = `<svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">
  <polygon points="32 4, 30 11, 26 7" fill="currentColor"/>
  <path d="M 28 9 A 13 13 0 1 1 12 9" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round"/>
  <text x="20" y="26" text-anchor="middle" font-size="12" font-weight="700" fill="currentColor" font-family="inherit">15</text>
</svg>`;

export class KFEAudioPlayer extends KFEComponent {
  // `completed` / `required` drive the playcount badge; `prevEnabled` /
  // `nextEnabled` drive the outer item-navigation arrows. `src` is the audio
  // URL. All are plain reactive props (a change funnels through _sync()).
  static props = ["src", "completed", "required", "prevEnabled", "nextEnabled"];

  static styles = `
    :host { display: block; }
    .player {
      position: relative;
      background: var(--kfe-surface);
      border: 1px solid var(--kfe-border);
      border-radius: 12px;
      padding: 1rem 1rem 0.85rem;
      box-sizing: border-box;
    }
    /* Playcount badge — top-right, white-on-dark pill per the iOS screenshot. */
    .badge {
      /* Lifted into the gutter above the card so the pill floats clear of the
         scrubber's right end instead of overlapping the track (Brief 146). */
      position: absolute; top: -0.7rem; right: 0.75rem;
      display: inline-flex; align-items: center;
      font-variant-numeric: tabular-nums;
      font-size: 0.8rem; font-weight: 700; line-height: 1;
      color: #fff; background: var(--kfe-accent);
      border-radius: 999px; padding: 0.3rem 0.6rem;
      min-width: 1.5rem; justify-content: center;
    }
    .badge[hidden] { display: none; }

    .scrub-row { display: flex; align-items: center; }
    input.scrub { flex: 1; width: 100%; accent-color: var(--kfe-accent); }
    .times {
      display: flex; justify-content: space-between;
      font-variant-numeric: tabular-nums;
      color: var(--kfe-muted); font-size: 0.75rem;
      margin: 0.1rem 0.1rem 0.6rem;
    }

    .transport {
      display: flex; align-items: center; justify-content: center;
      gap: 0.75rem;
    }
    button {
      flex: 0 0 auto;
      display: inline-flex; align-items: center; justify-content: center;
      border: 1px solid var(--kfe-border);
      background: transparent; color: var(--kfe-fg);
      border-radius: 50%;
      width: 2.75rem; height: 2.75rem;
      font-size: 0.95rem; line-height: 1;
      cursor: pointer;
      padding: 0;
    }
    button:disabled { opacity: var(--kfe-locked-opacity, 0.4); cursor: default; }
    /* The skip buttons read as a circular arrow wrapping "15" (iOS canon).
       Padding is reduced + font-size zeroed so the SVG fills the button cleanly
       without nudging from any residual text node. */
    button.skip { padding: 0; font-size: 0; }
    button.skip svg { width: 78%; height: 78%; display: block; }
    button.play {
      width: 3.25rem; height: 3.25rem;
      background: var(--kfe-accent); color: var(--kfe-on-accent, #fff);
      border-color: var(--kfe-accent); font-size: 1.15rem;
    }
    /* Nav chevrons — bigger + heavier so the «« / »» glyphs read clearly
       against the surface (the prior 1.1rem was too subtle on the deployed
       build; Chris 2026-06-09 smoke feedback). */
    button.nav {
      font-size: 1.6rem; font-weight: 800; line-height: 1;
      letter-spacing: -0.08em; padding: 0;
    }
  `;

  constructor() {
    super();
    this._built = false;
    /** @type {HTMLAudioElement} */
    this._audio = document.createElement("audio");
    this._audio.setAttribute("playsinline", "");
    this._audio.preload = "metadata";
    // Refs populated in _build().
    /** @type {HTMLInputElement | null} */  this._scrub = null;
    /** @type {HTMLElement | null} */       this._curEl = null;
    /** @type {HTMLElement | null} */       this._durEl = null;
    /** @type {HTMLElement | null} */       this._badge = null;
    /** @type {HTMLButtonElement | null} */ this._playBtn = null;
    /** @type {HTMLButtonElement | null} */ this._backBtn = null;
    /** @type {HTMLButtonElement | null} */ this._fwdBtn = null;
    /** @type {HTMLButtonElement | null} */ this._prevBtn = null;
    /** @type {HTMLButtonElement | null} */ this._nextBtn = null;
    this._scrubbing = false;
  }

  onConnected() {
    // The audio element is created in the constructor and lives across the
    // component's lifetime; tear it down on unmount (Common Pitfall #2).
    this.addCleanup(() => {
      const a = this._audio;
      try { a.pause(); } catch { /* no-op */ }
      a.removeAttribute("src");
      try { a.load(); } catch { /* no-op */ }
    });
  }

  /**
   * Imperative render: build the chrome once, then sync dynamic state. Returns
   * undefined so KFEComponent's `_renderNow` leaves the shadow root alone (a
   * `replaceChildren` would drop the live <audio> mid-play).
   * @returns {void}
   */
  render() {
    if (!this._built) this._build();
    this._sync();
  }

  _build() {
    const root = this.shadowRoot;
    if (!root) return;
    const ctor = /** @type {typeof KFEComponent} */ (this.constructor);
    const style = document.createElement("style");
    style.textContent = ctor.styles || "";

    const wrap = document.createElement("div");
    wrap.className = "player";

    // Playcount badge.
    const badge = document.createElement("span");
    badge.className = "badge";
    badge.setAttribute("role", "status");
    this._badge = badge;

    // Scrubber + time readout.
    const scrubRow = document.createElement("div");
    scrubRow.className = "scrub-row";
    const scrub = /** @type {HTMLInputElement} */ (document.createElement("input"));
    scrub.type = "range";
    scrub.className = "scrub";
    scrub.min = "0";
    scrub.max = "0.1";
    scrub.step = "0.1";
    scrub.value = "0";
    scrub.setAttribute("aria-label", t("audioPlayer.seek") || "Seek");
    scrub.addEventListener("input", () => {
      this._scrubbing = true;
      this._audio.currentTime = Number(scrub.value) || 0;
      this._syncTransport();
    });
    scrub.addEventListener("change", () => { this._scrubbing = false; });
    this._scrub = scrub;
    scrubRow.appendChild(scrub);

    const times = document.createElement("div");
    times.className = "times";
    const cur = document.createElement("span"); cur.textContent = "0:00";
    const dur = document.createElement("span"); dur.textContent = "0:00";
    times.append(cur, dur);
    this._curEl = cur; this._durEl = dur;

    // Transport row: « | −15 | ▶ | +15 | »
    const transport = document.createElement("div");
    transport.className = "transport";

    const prevBtn = this._makeButton("nav", "‹‹", "audioPlayer.previousItem", () => {
      if (/** @type {any} */ (this).prevEnabled) this.emit("prev-item");
    });
    const backBtn = this._makeButton("skip", "", "audioPlayer.skipBack15s", () => {
      this._skip(-SKIP_SECONDS);
    });
    // The SVG owns its own "15" text; the button's textContent stays empty so
    // the icon fills the button cleanly. aria-label still announces "Back 15
    // seconds" so the change is purely visual (Brief 138 polish).
    backBtn.innerHTML = SKIP_BACK_SVG;
    const playBtn = this._makeButton("play", "▶", "audioPlayer.play", () => this._togglePlay());
    const fwdBtn = this._makeButton("skip", "", "audioPlayer.skipForward15s", () => {
      this._skip(SKIP_SECONDS);
    });
    fwdBtn.innerHTML = SKIP_FORWARD_SVG;
    const nextBtn = this._makeButton("nav", "››", "audioPlayer.nextItem", () => {
      if (/** @type {any} */ (this).nextEnabled) this.emit("next-item");
    });
    this._prevBtn = prevBtn; this._backBtn = backBtn; this._playBtn = playBtn;
    this._fwdBtn = fwdBtn; this._nextBtn = nextBtn;
    transport.append(prevBtn, backBtn, playBtn, fwdBtn, nextBtn);

    wrap.append(badge, scrubRow, times, transport, this._audio);
    root.replaceChildren(style, wrap);

    // Audio events — imperative updates only (never full re-render).
    const a = this._audio;
    a.addEventListener("play", () => this._updatePlayButton());
    a.addEventListener("pause", () => this._updatePlayButton());
    a.addEventListener("timeupdate", () => this._syncTransport());
    a.addEventListener("loadedmetadata", () => this._syncTransport());
    a.addEventListener("durationchange", () => this._syncTransport());
    a.addEventListener("ended", () => {
      this._updatePlayButton();
      this._syncTransport();
      this.emit("ended");
    });

    this._built = true;
  }

  /**
   * @param {string} cls
   * @param {string} label visible glyph/text
   * @param {string} i18nKey aria-label key
   * @param {() => void} onClick
   * @returns {HTMLButtonElement}
   */
  _makeButton(cls, label, i18nKey, onClick) {
    const btn = /** @type {HTMLButtonElement} */ (document.createElement("button"));
    btn.type = "button";
    btn.className = cls;
    btn.textContent = label;
    btn.setAttribute("aria-label", t(i18nKey) || label);
    btn.addEventListener("click", onClick);
    return btn;
  }

  _togglePlay() {
    const a = this._audio;
    if (a.paused) {
      // First play must run inside this user gesture (iOS Safari).
      const p = a.play();
      if (p && typeof p.catch === "function") p.catch(() => { /* gesture/decode race */ });
    } else {
      a.pause();
    }
  }

  /** @param {number} delta seconds (±) */
  _skip(delta) {
    const a = this._audio;
    const dur = Number.isFinite(a.duration) ? a.duration : Infinity;
    const next = Math.max(0, Math.min(a.currentTime + delta, dur));
    a.currentTime = next;
    this._syncTransport();
  }

  /** Sync everything that depends on props (badge, nav arrows, src). */
  _sync() {
    if (!this._built) return;
    const self = /** @type {any} */ (this);
    // Audio source (only touch when it actually changes — setting `.src`
    // reloads the element).
    const src = typeof self.src === "string" ? self.src : "";
    if (src && this._audio.getAttribute("src") !== src) {
      this._audio.src = src;
    }

    // Playcount badge — completed/required (Brief 125's resolved value). Hidden
    // for non-completable content (required < 1).
    const required = Math.floor(Number(self.required) || 0);
    const completed = Math.max(0, Math.floor(Number(self.completed) || 0));
    if (this._badge) {
      if (required >= 1) {
        this._badge.hidden = false;
        this._badge.textContent = `${completed}/${required}`;
        this._badge.setAttribute(
          "aria-label",
          t("audioPlayer.playCountLabel", { completed, required }) || `${completed}/${required}`,
        );
      } else {
        this._badge.hidden = true;
      }
    }

    // Item-navigation arrows.
    if (this._prevBtn) this._prevBtn.disabled = !self.prevEnabled;
    if (this._nextBtn) this._nextBtn.disabled = !self.nextEnabled;

    this._syncTransport();
  }

  /** Sync scrubber, time readout, and skip-button bounds from the audio clock. */
  _syncTransport() {
    const a = this._audio;
    const cur = Number.isFinite(a.currentTime) ? a.currentTime : 0;
    const dur = Number.isFinite(a.duration) && a.duration > 0 ? a.duration : 0;

    if (this._scrub && !this._scrubbing) {
      this._scrub.max = String(Math.max(0.1, dur));
      this._scrub.value = String(cur);
    }
    if (this._curEl) this._curEl.textContent = fmt(cur);
    if (this._durEl) this._durEl.textContent = fmt(dur);

    // Bounds (Brief 138): currentTime ≤ 15 disables back; currentTime + 15 ≥
    // duration disables forward. Forward stays enabled until the duration is
    // known (dur === 0 → unknown, don't pre-disable).
    if (this._backBtn) this._backBtn.disabled = cur <= SKIP_SECONDS;
    if (this._fwdBtn) this._fwdBtn.disabled = dur > 0 && cur + SKIP_SECONDS >= dur;
  }

  _updatePlayButton() {
    const btn = this._playBtn;
    if (!btn) return;
    const playing = !this._audio.paused && !this._audio.ended;
    btn.textContent = playing ? "❚❚" : "▶";
    btn.setAttribute("aria-label", t(playing ? "audioPlayer.pause" : "audioPlayer.play") || (playing ? "Pause" : "Play"));
  }
}

/** @param {number} s seconds → "m:ss" */
function fmt(s) {
  if (!Number.isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${r}`;
}
